import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";

const PlanLimitContext = createContext(null);

export const usePlanLimits = () => {
  const context = useContext(PlanLimitContext);
  if (!context) {
    throw new Error("usePlanLimits must be used within a PlanLimitProvider");
  }
  return context;
};

export const PlanLimitProvider = ({ children }) => {
  const [limits, setLimits] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasActivePhone, setHasActivePhone] = useState(null);
  const phoneStatusRef = useRef(null);

  const getAuthConfig = () => {
    const token = localStorage.getItem("token");
    return { headers: token ? { Authorization: `Bearer ${token}` } : {} };
  };

  const fetchLimits = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/usage/limits`, getAuthConfig());
      setLimits(res.data);
    } catch (error) {
      if (error.response?.status !== 401) {
        console.error("Failed to fetch usage limits:", error);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const checkPhoneStatusLive = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/settings/phone-numbers`, getAuthConfig());
      const phones = res.data || [];
      const active = phones.length > 0 && phones.some(p => p.is_active !== false);
      setHasActivePhone(active);
      phoneStatusRef.current = active;
      return active;
    } catch {
      setHasActivePhone(null);
      phoneStatusRef.current = null;
      return null;
    }
  }, []);

  const fetchPhoneStatus = useCallback(async () => {
    await checkPhoneStatusLive();
  }, [checkPhoneStatusLive]);

  useEffect(() => {
    fetchLimits();
    fetchPhoneStatus();
  }, [fetchLimits, fetchPhoneStatus]);

  // Helper functions to check limits
  const canAddTeamMember = () => {
    if (!limits) return true;
    const { team_members } = limits.limits;
    return team_members.unlimited || team_members.used < team_members.limit;
  };

  const canAddPhoneLine = () => {
    if (!limits) return true;
    const { dedicated_lines } = limits.limits;
    return dedicated_lines.unlimited || dedicated_lines.used < dedicated_lines.limit;
  };

  const canSendSMS = () => {
    if (!limits) return true;
    const { sms } = limits.limits;
    return sms.unlimited || sms.used < sms.limit;
  };

  const canMakeCall = () => {
    if (!limits) return true;
    const { minutes } = limits.limits;
    return minutes.unlimited || minutes.used < minutes.limit;
  };

  const hasFeature = (featureName) => {
    if (!limits) return false;
    return limits.features?.[featureName] || false;
  };

  const getUsagePercent = (type) => {
    if (!limits) return 0;
    const item = limits.limits[type];
    if (!item || item.unlimited) return 0;
    return Math.min((item.used / item.limit) * 100, 100);
  };

  const isNearLimit = (type, threshold = 75) => {
    return getUsagePercent(type) >= threshold;
  };

  const isAtLimit = (type) => {
    return getUsagePercent(type) >= 100;
  };

  /**
   * Check if user can send SMS. Shows a toast alert if not ready.
   * Returns true if SMS is ready, false otherwise.
   * Does a live re-check if cached phone status is false to handle stale state.
   */
  const isSmsReady = (showAlert = true) => {
    // If we know there's an active phone, fast path
    if (hasActivePhone === true || phoneStatusRef.current === true) {
      if (!canSendSMS()) {
        if (showAlert) {
          toast.error("SMS limit reached. Please upgrade your plan or add balance to continue sending SMS.", { duration: 5000 });
        }
        return false;
      }
      return true;
    }
    // If status is null (unknown/loading), allow through — don't block on uncertainty
    if (hasActivePhone === null && phoneStatusRef.current === null) {
      // Trigger a background re-check for next time
      checkPhoneStatusLive();
      if (!canSendSMS()) {
        if (showAlert) {
          toast.error("SMS limit reached. Please upgrade your plan or add balance to continue sending SMS.", { duration: 5000 });
        }
        return false;
      }
      return true;
    }
    // If cached status is false, show error but also trigger re-check for next attempt
    if (hasActivePhone === false) {
      checkPhoneStatusLive();
      if (showAlert) {
        toast.error("No active phone number configured. Please purchase a phone number in Settings > Phone Numbers to send SMS.", { duration: 5000 });
      }
      return false;
    }
    if (!canSendSMS()) {
      if (showAlert) {
        toast.error("SMS limit reached. Please upgrade your plan or add balance to continue sending SMS.", { duration: 5000 });
      }
      return false;
    }
    return true;
  };

  /**
   * Async version: does a live phone check before deciding.
   * Use this for critical SMS operations where stale data could block users.
   */
  const isSmsReadyAsync = async (showAlert = true) => {
    // Always do a live check
    const liveStatus = await checkPhoneStatusLive();
    if (liveStatus === false) {
      if (showAlert) {
        toast.error("No active phone number configured. Please purchase a phone number in Settings > Phone Numbers to send SMS.", { duration: 5000 });
      }
      return false;
    }
    if (!canSendSMS()) {
      if (showAlert) {
        toast.error("SMS limit reached. Please upgrade your plan or add balance to continue sending SMS.", { duration: 5000 });
      }
      return false;
    }
    return true;
  };

  const value = {
    limits,
    loading,
    hasActivePhone,
    refetch: () => { fetchLimits(); fetchPhoneStatus(); },
    canAddTeamMember,
    canAddPhoneLine,
    canSendSMS,
    canMakeCall,
    hasFeature,
    getUsagePercent,
    isNearLimit,
    isAtLimit,
    isSmsReady,
    isSmsReadyAsync,
  };

  return (
    <PlanLimitContext.Provider value={value}>
      {children}
    </PlanLimitContext.Provider>
  );
};

export default PlanLimitContext;
